import { z } from "zod";

import {
  canonicalizeFeedbackCategoryIds,
  type FEEDBACK_TAXONOMY_VERSION,
  type FeedbackCategoryId,
  type FeedbackCategorySnapshot,
} from "./feedbackTaxonomy.js";

export const ECHO_FEEDBACK_SCHEMA_VERSION = "echo-feedback.v2" as const;
export const ECHO_FEEDBACK_METADATA_SCHEMA_VERSION_V1 =
  "echo-feedback-metadata.v1" as const;
// v2 adds the frozen `framing` correlation collector (CROP-WB-003).
export const ECHO_FEEDBACK_METADATA_SCHEMA_VERSION =
  "echo-feedback-metadata.v2" as const;

export type FeedbackMetadataSchemaVersion =
  | typeof ECHO_FEEDBACK_METADATA_SCHEMA_VERSION_V1
  | typeof ECHO_FEEDBACK_METADATA_SCHEMA_VERSION;

export const ECHO_FEEDBACK_LIMITS = {
  COMMENT_CHARACTERS: 4_000,
  ECHO_ID_CHARACTERS: 200,
  ECHO_NAME_CHARACTERS: 300,
  FRAME_DATA_URL_CHARACTERS: 2_100_000,
  METADATA_STRING_CHARACTERS: 6_000,
  METADATA_ARRAY_ITEMS: 50,
  METADATA_OBJECT_KEYS: 100,
  METADATA_DEPTH: 8,
  TIMESTAMP_TOLERANCE_MS: 1_000,
} as const;

export type FeedbackJsonPrimitive = string | number | boolean | null;
export type FeedbackJsonValue =
  | FeedbackJsonPrimitive
  | FeedbackJsonObject
  | FeedbackJsonValue[];
export type FeedbackJsonObject = { [key: string]: FeedbackJsonValue };

export type FeedbackEnvironment = {
  literalKey: "stg" | "staging";
  productMeaning: "customer-facing production" | "engineering environment";
};

export type FeedbackPosition = {
  timestampMs: number;
  videoDurationMs: number | null;
};

export type FeedbackPlayerObservation = FeedbackPosition & {
  videoWidth: number | null;
  videoHeight: number | null;
  playbackRate: number | null;
};

export type FeedbackCollectorStatus =
  | "complete"
  | "partial"
  | "unavailable"
  | "unavailable_legacy";

export type FeedbackMetadataStatus = FeedbackCollectorStatus;

export type FeedbackCollectorName =
  | "core"
  | "timeline"
  | "i2v"
  | "media"
  | "audio"
  | "configuration"
  | "framing";

export type FeedbackCollectorSummary = {
  status: FeedbackCollectorStatus;
  missing: string[];
};

export type FeedbackMetadataSnapshot = {
  status: FeedbackMetadataStatus;
  capturedAt: string;
  collectors: Partial<Record<FeedbackCollectorName, FeedbackCollectorSummary>> &
    Record<Exclude<FeedbackCollectorName, "framing">, FeedbackCollectorSummary>;
  core: FeedbackJsonObject;
  timeline: FeedbackJsonObject;
  i2v: FeedbackJsonObject;
  media: FeedbackJsonObject;
  audio: FeedbackJsonObject;
  configuration: FeedbackJsonObject;
  /** v2 frozen framing correlation; absent on v1 legacy snapshots. */
  framing?: FeedbackJsonObject;
};

export type FeedbackFrame =
  | {
      status: "stored";
      pathname: string;
      contentType: "image/jpeg";
    }
  | {
      status: "unavailable" | "capture_failed";
      pathname: null;
      contentType: null;
    };

export type FeedbackReporter = {
  userId: string;
  displayName: string | null;
  email: string | null;
};

export type FeedbackNotification = {
  status: "pending" | "sent" | "failed";
  attemptCount: number;
  lastAttemptAt: string | null;
  lastErrorCode: string | null;
};

export type EchoFeedbackReportV2 = {
  schemaVersion: typeof ECHO_FEEDBACK_SCHEMA_VERSION;
  feedbackKind: "operator_qc";
  audience: "internal";
  source: "crm_echo_player";
  id: string;
  clientRequestId: string;
  environment: FeedbackEnvironment;
  echo: {
    id: string;
    name: string;
  };
  position: FeedbackPosition;
  comment: string;
  categories: FeedbackCategorySnapshot[];
  taxonomyVersion: typeof FEEDBACK_TAXONOMY_VERSION;
  metadataSchemaVersion: FeedbackMetadataSchemaVersion;
  metadata: FeedbackMetadataSnapshot;
  frame: FeedbackFrame;
  reporter: FeedbackReporter;
  notification: FeedbackNotification;
  submittedAt: string;
};

export type FeedbackSubmissionInput = {
  schemaVersion: typeof ECHO_FEEDBACK_SCHEMA_VERSION;
  metadataSchemaVersion: FeedbackMetadataSchemaVersion;
  clientRequestId: string;
  echoId: string;
  timestampMs: number;
  videoDurationMs: number | null;
  comment: string;
  categoryIds: FeedbackCategoryId[];
  frameDataUrl: string | null;
  metadata: FeedbackMetadataSnapshot;
};

const submissionSchema = z
  .object({
    schemaVersion: z.literal(ECHO_FEEDBACK_SCHEMA_VERSION),
    metadataSchemaVersion: z.union([
      z.literal(ECHO_FEEDBACK_METADATA_SCHEMA_VERSION_V1),
      z.literal(ECHO_FEEDBACK_METADATA_SCHEMA_VERSION),
    ]),
    clientRequestId: z.string().uuid(),
    echoId: z
      .string()
      .trim()
      .min(1)
      .max(ECHO_FEEDBACK_LIMITS.ECHO_ID_CHARACTERS),
    timestampMs: z.number().int().min(0).max(86_400_000),
    videoDurationMs: z.number().int().min(0).max(86_400_000).nullable(),
    comment: z
      .string()
      .trim()
      .min(1)
      .max(ECHO_FEEDBACK_LIMITS.COMMENT_CHARACTERS),
    categoryIds: z.array(z.unknown()).max(9),
    frameDataUrl: z
      .string()
      .max(ECHO_FEEDBACK_LIMITS.FRAME_DATA_URL_CHARACTERS)
      .regex(/^data:image\/jpeg;base64,[A-Za-z0-9+/=\r\n]+$/)
      .nullable(),
    metadata: z.unknown(),
  })
  .strict();

/**
 * Parse and canonicalize the browser payload. Authoritative reporter, Echo
 * display name, environment, submission time and delivery status deliberately
 * do not exist in this contract; the authenticated server supplies them.
 */
export function parseFeedbackSubmissionInput(
  value: unknown,
): FeedbackSubmissionInput {
  const parsed = submissionSchema.parse(value);
  const categoryIds = canonicalizeFeedbackCategoryIds(parsed.categoryIds);
  const metadata = parseFeedbackMetadataSnapshot(parsed.metadata);
  const { timestampMs, videoDurationMs } = parsed;
  if (
    videoDurationMs !== null &&
    timestampMs > videoDurationMs + ECHO_FEEDBACK_LIMITS.TIMESTAMP_TOLERANCE_MS
  ) {
    throw new TypeError(
      "Feedback timestamp exceeds the observed video duration",
    );
  }
  return {
    schemaVersion: parsed.schemaVersion,
    metadataSchemaVersion: parsed.metadataSchemaVersion,
    clientRequestId: parsed.clientRequestId,
    echoId: parsed.echoId,
    timestampMs,
    videoDurationMs: videoDurationMs ?? null,
    comment: parsed.comment,
    categoryIds,
    frameDataUrl: parsed.frameDataUrl ?? null,
    metadata,
  };
}

export function secondsToTimestampMs(seconds: number): number {
  if (!Number.isFinite(seconds) || seconds < 0) {
    throw new TypeError("Video timestamp must be a finite non-negative number");
  }
  return Math.round(seconds * 1_000);
}

const BASE_COLLECTOR_NAMES = [
  "core",
  "timeline",
  "i2v",
  "media",
  "audio",
  "configuration",
] as const satisfies readonly FeedbackCollectorName[];

const V2_COLLECTOR_NAMES = [
  ...BASE_COLLECTOR_NAMES,
  "framing",
] as const satisfies readonly FeedbackCollectorName[];

const COLLECTOR_STATUSES: readonly FeedbackCollectorStatus[] = [
  "complete",
  "partial",
  "unavailable",
  "unavailable_legacy",
];

export function parseFeedbackMetadataSnapshot(
  value: unknown,
): FeedbackMetadataSnapshot {
  if (!isRecord(value)) {
    throw new TypeError("Feedback metadata must be an object");
  }
  // Legacy v1 payloads have six sections; v2 adds `framing`. Both remain
  // parseable so old feedback and old clients keep working (CROP-WB-003).
  const collectorNames: readonly FeedbackCollectorName[] =
    "framing" in value ? V2_COLLECTOR_NAMES : BASE_COLLECTOR_NAMES;
  assertExactKeys(value, [
    "status",
    "capturedAt",
    "collectors",
    ...collectorNames,
  ]);
  if (!COLLECTOR_STATUSES.includes(value.status as FeedbackCollectorStatus)) {
    throw new TypeError("Invalid feedback metadata status");
  }
  if (
    typeof value.capturedAt !== "string" ||
    !isIsoTimestamp(value.capturedAt)
  ) {
    throw new TypeError("Invalid feedback metadata capture time");
  }
  if (!isRecord(value.collectors)) {
    throw new TypeError("Feedback metadata collectors must be an object");
  }
  assertExactKeys(value.collectors, collectorNames);
  const collectors = {} as Record<
    FeedbackCollectorName,
    FeedbackCollectorSummary
  >;
  for (const name of collectorNames) {
    const collector = value.collectors[name];
    if (!isRecord(collector)) {
      throw new TypeError(`Feedback metadata collector ${name} is invalid`);
    }
    assertExactKeys(collector, ["status", "missing"]);
    if (
      !COLLECTOR_STATUSES.includes(collector.status as FeedbackCollectorStatus)
    ) {
      throw new TypeError(
        `Feedback metadata collector ${name} has invalid status`,
      );
    }
    if (
      !Array.isArray(collector.missing) ||
      collector.missing.length > ECHO_FEEDBACK_LIMITS.METADATA_ARRAY_ITEMS ||
      !collector.missing.every(isReasonCode)
    ) {
      throw new TypeError(
        `Feedback metadata collector ${name} has invalid reasons`,
      );
    }
    collectors[name] = {
      status: collector.status as FeedbackCollectorStatus,
      missing: [...collector.missing] as string[],
    };
  }

  const sections = {} as Record<FeedbackCollectorName, FeedbackJsonObject>;
  for (const name of collectorNames) {
    const section = value[name];
    if (!isRecord(section)) {
      throw new TypeError(`Feedback metadata section ${name} is invalid`);
    }
    assertJsonValue(section, 0);
    sections[name] = section as FeedbackJsonObject;
  }

  return {
    status: value.status as FeedbackMetadataStatus,
    capturedAt: value.capturedAt,
    collectors,
    ...sections,
  };
}

function assertJsonValue(value: unknown, depth: number): void {
  if (depth > ECHO_FEEDBACK_LIMITS.METADATA_DEPTH) {
    throw new TypeError("Feedback metadata exceeds the maximum depth");
  }
  if (value === null || typeof value === "boolean") return;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new TypeError("Feedback metadata numbers must be finite");
    }
    return;
  }
  if (typeof value === "string") {
    if (value.length > ECHO_FEEDBACK_LIMITS.METADATA_STRING_CHARACTERS) {
      throw new TypeError("Feedback metadata string is too long");
    }
    return;
  }
  if (Array.isArray(value)) {
    if (value.length > ECHO_FEEDBACK_LIMITS.METADATA_ARRAY_ITEMS) {
      throw new TypeError("Feedback metadata array is too long");
    }
    value.forEach((item) => assertJsonValue(item, depth + 1));
    return;
  }
  if (isRecord(value)) {
    const entries = Object.entries(value);
    if (entries.length > ECHO_FEEDBACK_LIMITS.METADATA_OBJECT_KEYS) {
      throw new TypeError("Feedback metadata object has too many keys");
    }
    entries.forEach(([, item]) => assertJsonValue(item, depth + 1));
    return;
  }
  throw new TypeError("Feedback metadata must contain JSON values only");
}

function assertExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
): void {
  const allowed = new Set(expected);
  const unknown = Object.keys(value).find((key) => !allowed.has(key));
  const missing = expected.find((key) => !(key in value));
  if (unknown || missing) {
    throw new TypeError(
      `Invalid feedback metadata shape${unknown ? `; unknown ${unknown}` : ""}${missing ? `; missing ${missing}` : ""}`,
    );
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isReasonCode(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= 100 &&
    /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/.test(value)
  );
}

function isIsoTimestamp(value: string): boolean {
  return Number.isFinite(Date.parse(value));
}
